import React, { useState } from 'react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { BarChart2Icon, FilterIcon, CheckIcon, AlertTriangleIcon, XIcon, DownloadIcon } from 'lucide-react';
export function UsageTracking({
  locale
}) {
  // State for tracking filters
  const [filters, setFilters] = useState({
    entity: 'all',
    site: 'all',
    metric: 'all',
    period: '30'
  });
  // Mock usage data
  const usageData = [{
    id: 'usage-1',
    metricName: 'Documents',
    entityName: 'Global Technologies Inc.',
    siteName: 'New York HQ',
    usageCount: 75420,
    limit: 100000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Near Limit'
  }, {
    id: 'usage-2',
    metricName: 'Invoices',
    entityName: 'Global Technologies Inc.',
    siteName: 'New York HQ',
    usageCount: 22456,
    limit: 50000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'OK'
  }, {
    id: 'usage-3',
    metricName: 'Vendors',
    entityName: 'Global Technologies Inc.',
    siteName: 'New York HQ',
    usageCount: 342,
    limit: 500,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'OK'
  }, {
    id: 'usage-4',
    metricName: 'Invoices',
    entityName: 'European Operations Ltd',
    siteName: 'All Sites',
    usageCount: 48500,
    limit: 50000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Near Limit'
  }, {
    id: 'usage-5',
    metricName: 'Users',
    entityName: 'Global Technologies Inc.',
    siteName: 'All Sites',
    usageCount: 48,
    limit: 50,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Near Limit'
  }, {
    id: 'usage-6',
    metricName: 'Data Points',
    entityName: 'Global Technologies Inc.',
    siteName: 'All Sites',
    usageCount: 262500,
    limit: 250000,
    lastUpdated: '2023-10-22T14:30:00Z',
    status: 'Exceeded'
  }];
  // Daily usage data for chart
  const dailyUsageData = [{
    date: '2023-10-01',
    documents: 2100,
    invoices: 750,
    vendors: 10
  }, {
    date: '2023-10-02',
    documents: 2300,
    invoices: 800,
    vendors: 12
  }, {
    date: '2023-10-03',
    documents: 2200,
    invoices: 780,
    vendors: 8
  }, {
    date: '2023-10-04',
    documents: 2400,
    invoices: 820,
    vendors: 15
  }, {
    date: '2023-10-05',
    documents: 2500,
    invoices: 850,
    vendors: 9
  }, {
    date: '2023-10-06',
    documents: 2350,
    invoices: 790,
    vendors: 11
  }, {
    date: '2023-10-07',
    documents: 2150,
    invoices: 760,
    vendors: 7
  }, {
    date: '2023-10-08',
    documents: 2600,
    invoices: 880,
    vendors: 14
  }, {
    date: '2023-10-09',
    documents: 2700,
    invoices: 900,
    vendors: 16
  }, {
    date: '2023-10-10',
    documents: 2800,
    invoices: 950,
    vendors: 13
  }, {
    date: '2023-10-11',
    documents: 2750,
    invoices: 920,
    vendors: 10
  }, {
    date: '2023-10-12',
    documents: 2650,
    invoices: 890,
    vendors: 9
  }, {
    date: '2023-10-13',
    documents: 2900,
    invoices: 980,
    vendors: 17
  }, {
    date: '2023-10-14',
    documents: 3000,
    invoices: 1000,
    vendors: 15
  }];
  // Handle filter changes
  const handleFilterChange = e => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  // Filter the usage data based on selected filters
  const filteredData = usageData.filter(item => {
    return (filters.entity === 'all' || item.entityName === filters.entity) && (filters.site === 'all' || item.siteName === filters.site) && (filters.metric === 'all' || item.metricName === filters.metric);
  });
  // Get status badge styling
  const getStatusBadge = status => {
    switch (status) {
      case 'OK':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          icon: <CheckIcon className="h-4 w-4 mr-1" data-id="element-15854" />
        };
      case 'Near Limit':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          icon: <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-15855" />
        };
      case 'Exceeded':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          icon: <XIcon className="h-4 w-4 mr-1" data-id="element-15856" />
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          icon: null
        };
    }
  };
  return <div className="space-y-6" data-id="element-15857">
      <div className="flex justify-between items-center" data-id="element-15858">
        <div data-id="element-15859">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-15860">Usage Tracking</h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-15861">
            Monitor your system usage across entities, sites, and metrics
          </p>
        </div>
        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15862">
          <DownloadIcon className="h-4 w-4 mr-2" data-id="element-15863" />
          Export Report
        </button>
      </div>
      {/* Filters */}
      <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6" data-id="element-15864">
        <div className="md:grid md:grid-cols-4 md:gap-6" data-id="element-15865">
          <div className="md:col-span-1" data-id="element-15866">
            <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-15867">
              Filters
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-15868">
              Filter usage data by entity, site, metric and time period
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3" data-id="element-15869">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-15870">
              <div className="sm:col-span-3" data-id="element-15871">
                <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-15872">
                  Entity
                </label>
                <div className="mt-1" data-id="element-15873">
                  <select id="entity" name="entity" value={filters.entity} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15874">
                    <option value="all" data-id="element-15875">All Entities</option>
                    <option value="Global Technologies Inc." data-id="element-15876">
                      Global Technologies Inc.
                    </option>
                    <option value="European Operations Ltd" data-id="element-15877">
                      European Operations Ltd
                    </option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15878">
                <label htmlFor="site" className="block text-sm font-medium text-gray-700" data-id="element-15879">
                  Site
                </label>
                <div className="mt-1" data-id="element-15880">
                  <select id="site" name="site" value={filters.site} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15881">
                    <option value="all" data-id="element-15882">All Sites</option>
                    <option value="New York HQ" data-id="element-15883">New York HQ</option>
                    <option value="San Francisco Office" data-id="element-15884">
                      San Francisco Office
                    </option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15885">
                <label htmlFor="metric" className="block text-sm font-medium text-gray-700" data-id="element-15886">
                  Metric
                </label>
                <div className="mt-1" data-id="element-15887">
                  <select id="metric" name="metric" value={filters.metric} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15888">
                    <option value="all" data-id="element-15889">All Metrics</option>
                    <option value="Documents" data-id="element-15890">Documents</option>
                    <option value="Invoices" data-id="element-15891">Invoices</option>
                    <option value="Vendors" data-id="element-15892">Vendors</option>
                    <option value="Users" data-id="element-15893">Users</option>
                    <option value="Data Points" data-id="element-15894">Data Points</option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15895">
                <label htmlFor="period" className="block text-sm font-medium text-gray-700" data-id="element-15896">
                  Time Period
                </label>
                <div className="mt-1" data-id="element-15897">
                  <select id="period" name="period" value={filters.period} onChange={handleFilterChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15898">
                    <option value="7" data-id="element-15899">Last 7 days</option>
                    <option value="30" data-id="element-15900">Last 30 days</option>
                    <option value="90" data-id="element-15901">Last 90 days</option>
                    <option value="365" data-id="element-15902">Last year</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Usage Chart */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-15903">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-15904">
          <div data-id="element-15905">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15906">
              Usage Trend
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15907">
              Daily usage trend for the selected time period
            </p>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-15908">
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center" data-id="element-15909">
            <div className="text-center" data-id="element-15910">
              <BarChart2Icon className="mx-auto h-12 w-12 text-gray-400" data-id="element-15911" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-15912">
                Usage chart visualization
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-15913">
                This would display a chart of usage trends over time
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Usage Table */}
      <div className="flex flex-col" data-id="element-15914">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-15915">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-15916">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-15917">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-15918">
                <thead className="bg-gray-50" data-id="element-15919">
                  <tr data-id="element-15920">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15921">
                      Metric
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15922">
                      Entity / Site
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15923">
                      Usage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15924">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15925">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-15926">
                  {filteredData.map(item => {
                  const statusBadge = getStatusBadge(item.status);
                  const usagePercentage = Math.round(item.usageCount / item.limit * 100);
                  return <tr key={item.id} data-id="element-15927">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15928">
                          <div className="text-sm font-medium text-gray-900" data-id="element-15929">
                            {item.metricName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15930">
                          <div className="text-sm text-gray-900" data-id="element-15931">
                            {item.entityName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-15932">
                            {item.siteName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15933">
                          <div className="text-sm text-gray-900" data-id="element-15934">
                            {item.usageCount.toLocaleString()} /{' '}
                            {item.limit.toLocaleString()}
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full mt-2" data-id="element-15935">
                            <div className={`h-full rounded-full ${usagePercentage >= 90 ? 'bg-red-500' : usagePercentage >= 75 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{
                          width: `${usagePercentage}%`
                        }} data-id="element-15936"></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1" data-id="element-15937">
                            {usagePercentage}% used
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15938">
                          <div className="text-sm text-gray-900" data-id="element-15939">
                            {new Date(item.lastUpdated).toLocaleDateString(locale)}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-15940">
                            {new Date(item.lastUpdated).toLocaleTimeString(locale)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15941">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`} data-id="element-15942">
                            {statusBadge.icon}
                            {item.status}
                          </span>
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
              {filteredData.length === 0 && <div className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-15943">
                  No usage data found for the selected filters.
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}